import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import _ from "@lodash";
import validator from "validator";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useLayoutEffect, useState } from "react";
import {
	Alert,
	Checkbox,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Rating,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import jwtService from "../../services/jwtService";
import { Stack } from "@mui/system";
import { ratingColors } from "../../../../../@fuse/colors";
import HTMLReactParser from "html-react-parser";
import FuseSplashScreen from "@fuse/core/FuseSplashScreen";
import { SignupFormType } from "../../types/plan.type";
import JwtService from "../../services";
import ResumeSignupNotice from "./ResumeSignupNotice";
import {
	buildSignupFormPath,
	buildSignupRequest,
	resolveSignupEmail,
	smsConsentSchema,
} from "./signup.logic";

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
	firstName: yup.string().required("You must enter first name"),
	lastName: yup
		.string()
		.max(255, "Last Name can't be more than 255 characters long")
		.required("You must enter last name"),
	email: yup
		.string()
		.email("You must enter a valid email")
		.required("You must enter a email"),
	phoneNumber: yup
		.string()
		.max(19, "Phone Number can't be more than 19 characters long")
		.test("is-phone-valid", "Invalid phone number.", (value) => {
			if (value && value.trim() !== "") {
				return validatePhoneNumber(value);
			}
			return true;
		})
		.required("You must enter a Mobile Phone"),
	smsConsent: smsConsentSchema,
	password: yup
		.string()
		.max(255, "Password can't be more than 255 characters long")
		.required("Please enter your password.")
		.matches(
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*\?_\-])(?=.{8,})/,
			"Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
		),
});
const CustomPrimaryButton = ({
	bgClass,
	roundedClass,
	children,
	disabled = false,
	onClick,
}) => {
	const bgClassName = bgClass ? bgClass : "bg-peeva-yellow";
	const roundedClassName = roundedClass ? roundedClass : "rounded-[32px]";
	const className = `w-full sm:w-auto sm:h-56px h-48px ${roundedClassName} ${bgClassName} border py-10 sm:text-16px font-700 sm:leading-[18.75px] text-14px leading-16px text-peeva-black`;
	return (
		<Button
			variant="contained"
			className={className}
			aria-label={children}
			type="submit"
			size="large"
			onClick={onClick}
		>
			{children}
		</Button>
	);
};

const validatePhoneNumber = (number) => {
	const isValidPhoneNumber = validator.isMobilePhone(number);
	return isValidPhoneNumber;
};

/**
 * The sign up page.
 */
function SignUpForm() {
	const [searchParams] = useSearchParams();
	const storedForm = getStoredForm();
	const prefilledEmail = resolveSignupEmail(
		searchParams,
		storedForm.email || sessionStorage.getItem("email") || ""
	);
	const defaultValues = {
		firstName: storedForm.firstName ?? "",
		lastName: storedForm.lastName ?? "",
		email: prefilledEmail,
		phoneNumber: storedForm.phoneNumber ?? "",
		smsConsent: storedForm.smsConsent ?? false,
		password: storedForm.password ?? "",
	};
	const navigate = useNavigate();
	const { control, formState, trigger, handleSubmit, setError } = useForm({
		mode: "onChange",
		defaultValues,
		resolver: yupResolver(schema),
	});

	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [customError, setCustomError] = useState("");
	const [resumeSignup, setResumeSignup] = useState(false);

	const { isValid, dirtyFields, errors } = formState;

	const promoParam = searchParams.get("promo");
	const promoSuffix = promoParam ? `?promo=${promoParam}` : "";

	useLayoutEffect(() => {
		JwtService.clearHistory();
		onBackFromPetRegister();
	}, [prefilledEmail]);

	async function onBackFromPetRegister() {
		if (sessionStorage.getItem("signup")) {
			const res = await JwtService.deactivateUser();
			setLoading(false);
			if (res.succeeded) {
				const session = sessionStorage.getItem("signup");
				if (session) {
					const { email } = JSON.parse(session);
					sessionStorage.setItem("email", email);
					navigate(buildSignupFormPath(promoParam));
				}
			}
			sessionStorage.removeItem("signup");
		}
	}

	function onSubmit({
		firstName,
		lastName,
		email,
		phoneNumber,
		password,
		smsConsent,
	}: typeof defaultValues) {
		setLoading(true);
		setResumeSignup(false);
		const signupForm = {
			firstName,
			lastName,
			email,
			phoneNumber,
			password,
			smsConsent,
		};
		const userObj = buildSignupRequest(signupForm);

		sessionStorage.setItem("signup", JSON.stringify(signupForm));

		jwtService
			.createUser(userObj)
			.then((res) => {
				setLoading(false);
				const promo = searchParams.get("promo");
				navigate(`/pet-register${promo ? `?promo=${promo}` : ""}`);
				// No need to do anything, registered user data will be set at app/auth/AuthContext
			})
			.catch(
				(
					_errors: {
						type: "email" | "password" | "resume_signup" | `root.${string}` | "root";
						message: string;
					}[]
				) => {
					setLoading(false);
					if (_errors[0].type === "resume_signup") {
						sessionStorage.setItem("email", email);
						sessionStorage.removeItem("signup");
						setCustomError("");
						setResumeSignup(true);
						return;
					}
					setCustomError(_errors[0].message);

					setTimeout(() => {
						setCustomError("");
					}, 5000);
				}
			);
	}

	const handleShowPassword = () => {
		setShowPassword(!showPassword);
	};

	function getStoredForm(): SignupFormType {
		if (sessionStorage.getItem("signup")) {
			const form = JSON.parse(
				sessionStorage.getItem("signup")
			) as SignupFormType;

			return form;
		}
		return {} as SignupFormType;
	}


	return (
		<>
			<div className="flex flex-col min-w-0 min-h-screen justify-center bg-white relative">
				<div className="flex flex-row min-w-screen pt-20 h-56 border-after border-after-46 relative">
					<div
						className="flex items-center pl-10 w-1/4 relative cursor-pointer border-b-3 h-56px"
						onClick={() => navigate(`/sign-up${promoSuffix}`)}
					>
						<ArrowBackIosIcon className="sm:text-16px text-14px font-700 leading-18px text-peeva-black-110" />{" "}
						<Typography className="sm:text-16px font-700 sm:leading-18px text-peeva-black-110 text-14px leading-16px">
							{" "}
							Back
						</Typography>
					</div>
					<div className="flex items-start justify-center w-2/4 h-56px border-b-1">
						<img
							className="h-40px w-[110px] flex mx-auto"
							src="assets/img/checkout-logo.svg"
							alt="logo"
						/>
					</div>
					<div className="flex items-start w-1/4 h-56px border-b-1"></div>
				</div>
				<div className="h-full w-full pt-64 px-16 py-8 justify-center sm:p-48 md:flex md:h-full md:p-64">
					<div className="mx-auto max-w-xl sm:mx-0 sm:w-480 w-320 relative">
						{customError && (
							<Stack className="pb-12" sx={{ width: "100%" }} spacing={2}>
								<Alert variant="filled" severity="error" onClose={() => {}}>
									<div>{HTMLReactParser(customError)}</div>
								</Alert>
							</Stack>
						)}
						{loading && <FuseSplashScreen />}
						{resumeSignup && <ResumeSignupNotice promo={promoParam} />}
						<form
							name="registerForm"
							noValidate
							className="mt-32 flex w-full flex-col justify-center"
							id="signupform-form"
							onSubmit={handleSubmit(onSubmit)}
						>
							<div className="flex gap-10">
								<Controller
									name="firstName"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											className="mb-20 sm:mb-24 bg-[#F1F1F1] rounded-16 h-[58px] text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
											label="First name"
											autoFocus
											autoComplete="given-name"
											type="name"
											error={!!errors.firstName}
											helperText={errors?.firstName?.message}
											required
											fullWidth
											inputProps={{ maxLength: 255 }}
											InputLabelProps={{
												style: {
													lineHeight: "4rem",
												},
											}}
										/>
									)}
								/>
								<Controller
									name="lastName"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											className="mb-20 sm:mb-24 bg-[#F1F1F1] rounded-16 h-[58px] text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
											label="Last name"
											type="name"
											autoComplete="family-name"
											error={!!errors.lastName}
											helperText={errors?.lastName?.message}
											required
											fullWidth
											inputProps={{ maxLength: 255 }}
											InputLabelProps={{
												style: {
													lineHeight: "4rem",
												},
											}}
										/>
									)}
								/>
							</div>
							{!prefilledEmail && (
								<Controller
									name="email"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											className="mb-20 sm:mb-24 bg-[#F1F1F1] rounded-16 h-[58px] text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
											label="Email"
											type="email"
											error={!!errors.email}
											helperText={errors?.email?.message}
											required
											fullWidth
											inputProps={{ maxLength: 255 }}
											InputLabelProps={{
												style: {
													lineHeight: "4rem",
												},
											}}
										/>
									)}
								/>
							)}

							<Controller
								name="phoneNumber"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										className="mb-20 sm:mb-24 bg-[#F1F1F1] rounded-16 h-[58px] text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
										label="Mobile Phone"
										autoComplete="tel"
										type="tel"
										error={!!errors.phoneNumber}
										helperText={errors?.phoneNumber?.message}
										required
										fullWidth
										inputProps={{ maxLength: 255 }}
										InputLabelProps={{
											style: {
												lineHeight: "4rem",
											},
										}}
									/>
								)}
							/>
							<Typography component="div" className="mb-24 text-12px font-400 leading-14px text-peeva-black md:text-left text-center">
								Used to instantly notify you of your pet's location when their
								microchip is scanned
							</Typography>
							<Controller
								name="smsConsent"
								control={control}
								render={({ field }) => (
									<FormControl className="mb-20 sm:mb-24">
										<FormControlLabel
											className="m-0 items-start"
											control={(
												<Checkbox
													{...field}
													checked={field.value}
													onChange={(event) => field.onChange(event.target.checked)}
													inputProps={{ "aria-required": false }}
												/>
											)}
											label={(
												<Typography
													component="span"
													className="pt-9 text-12px font-400 leading-16px text-peeva-black"
												>
													By checking this box, I agree to receive recurring transactional text
													messages from Peeva at the mobile number provided, including microchip
													registration confirmations, renewal and billing reminders, and
													lost/found pet notifications. Message frequency varies. Message and
													data rates may apply. Reply STOP to unsubscribe or HELP for help.
													Consent is not a condition of purchase. See our{" "}
													<a
														href="https://peeva.co/terms-of-service/"
														className="!underline"
														target="_blank"
														rel="noopener noreferrer"
														onClick={(event) => event.stopPropagation()}
													>
														Terms of Service
													</a>{" "}
													and{" "}
													<a
														href="https://peeva.co/privacy/"
														className="!underline"
														target="_blank"
														rel="noopener noreferrer"
														onClick={(event) => event.stopPropagation()}
													>
														Privacy Policy
													</a>
													.
												</Typography>
											)}
										/>
									</FormControl>
								)}
							/>

							<Controller
								name="password"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										className="mb-20 sm:mb-24 bg-[#F1F1F1] rounded-16 h-[58px] text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
										label="Create Password"
										type={showPassword ? "text" : "password"}
										error={!!errors.password}
										helperText={errors?.password?.message}
										required
										autoComplete="new-password"
										fullWidth
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={handleShowPassword}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											),
										}}
										InputLabelProps={{
											style: {
												lineHeight: "4rem",
											},
										}}
									/>
								)}
							/>
							<CustomPrimaryButton
								bgClass="bg-peeva-blue hover:bg-peeva-blue-110 transition-colors duration-200 text-white"
								roundedClass="rounded-[16px]"
								disabled={loading}
								onClick={() => {
									trigger();
								}}
							>
								Continue to pet info
							</CustomPrimaryButton>

							<Typography component="div" className="mt-12 text-12px font-400 leading-14px text-peeva-black-120 md:text-left text-center">
								Peeva cares about your privacy. Your data will only be used to
								process your order and support your experience, as described in
								our
								<a
									href="https://peeva.co/privacy"
									className="!underline pl-2"
									role="button"
									target="_blank"
								>
									Privacy Policy
								</a>
								. Peeva will never sell, trade, or transfer your personal data
								to third-parties.
							</Typography>
							<div className="grid grid-cols-1 mt-20">
								<img
									className="h-[132px] w-auto pb-24 sm:pb-12 mx-auto"
									src="assets/img/googleReview2.png"
									alt="logo"
								/>
								<Typography component="div" className="text-black text-12px font-700 leading-18px block mt-12 md:text-left text-center">
									“Thank you sooooo much for reuniting our doggie back to us. We
									are so happy and grateful she was in good care and safe. What
									a blessing to have her safe at home 🙏💕”
								</Typography>
								<Typography component="div" className="text-black block text-center text-12px font-400 leading-16px mt-6">
									- Lelannie D
								</Typography>
							</div>
							<div className="flex justify-center gap-2 mt-32">
								<img className="flex" src="assets/img/secure.svg" alt="logo" />
								<img className="flex" src="assets/img/stripe.svg" alt="logo" />
							</div>
						</form>
					</div>
				</div>
			</div>

			<div className="bg-peeva-blue flex flex-col md:flex-row h-auto md:h-[100vh] w-full overflow-hidden">

				{/* image */}
				<div className="flex justify-center items-center min-h-screen p-4">
					<img
						className="w-full max-w-[500px] md:max-w-[700px] h-auto"
						src="assets/img/signupIcon4.svg"
						alt="logo"
					/>
				</div>
									 
				{/* review */}
				<div className="w-full md:w-1/2 flex items-center justify-end p-8 md:p-16">
					<div className="tracking-tight text-gray-400 text-20px font-700 leading-28px max-w-[600px]">
						<img
							className="h-[132px] w-auto pb-24 sm:pb-12 mx-auto"
							src="assets/img/googleReview3.png"
							alt="logo"
						/>
						<Typography component="div" className="text-black my-8 sm:text-20px font-700 sm:leading-28px text-14px leading-20px">
							“Thank you sooooo much for reuniting our doggie back to us. We are
							so happy and grateful she was in good care and safe. What a
							blessing to have her safe at home 🙏💕”
						</Typography>
						<Typography  component="div" className="text-black flex mt-5 sm:text-16px font-400 sm:leading-22px text-14px leading-20px">
							- Lelannie D.
						</Typography>
					</div>
				</div>

			</div>
		</>
	);
}

export default SignUpForm;
